import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, BadgeCheck, ChevronRight, Loader2, Plus, ScanSearch } from 'lucide-react';

import type { IpttqRecord, IpttqSuggestNewResult } from '@kelilearning/psycheflow-contracts';
import {
  formatIpttqBankMetaLine,
  hasCompleteGeneratedIpttq,
  ipttqIskWarningLabel,
  ipttqNeedsBankWarning,
  ipttqNeedsIskWarning,
  isIpttqSessionOriginalsRef,
  isIpttqToolbarReady,
} from '@kelilearning/psycheflow-contracts';

import { ipttqService } from '../../services/ipttqService';
import { stripIpttqDomainTerms } from './ipttqLearnerFacingText';
import { IskToolbarThumb, ipttqHasIskPreview } from './IskToolbarThumb';
import {
  countDriftedIpttqRecords,
  IPTTQ_SCOPE_DRIFT_TIP,
  isIpttqScopeDrifted,
} from './ipttqScopeDrift';

type Props = {
  ttqaId: string;
  initialRecords?: IpttqRecord[] | null;
  /** 品题范畴题量（仅用于创建按钮禁用等，不再触发自动扫描） */
  particleCount?: number;
  /** 品题会话 updatedAt：加入/移出颗粒等会刷新 */
  sessionUpdatedAt?: string | null;
  /** 各会话颗粒的 addedAt，用于「最近加入」 */
  particleAddedAts?: string[];
  /** 当前会话全部颗粒 ID 的稳定 key，用于检测浓缩结果范畴漂移 */
  sessionParticleKey?: string;
};

function CreatingOverlay({ title, detail }: { title: string; detail: string }) {
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 px-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface px-5 py-6 shadow-xl">
        <div className="flex items-start gap-3">
          <Loader2 size={22} className="mt-0.5 shrink-0 animate-spin text-primary" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">{title}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted">{detail}</p>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function pickLatestIso(values: Array<string | null | undefined>): string | null {
  let bestMs = 0;
  let bestIso: string | null = null;
  for (const value of values) {
    const iso = String(value || '').trim();
    if (!iso) continue;
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms) || ms <= bestMs) continue;
    bestMs = ms;
    bestIso = iso;
  }
  return bestIso;
}

function formatMetaTime(iso: string | null | undefined): string {
  const value = String(iso || '').trim();
  if (!value) return '暂无';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '暂无';
  return date.toLocaleString('zh-CN', { hour12: false });
}

function suggestionNeedsAttention(result: IpttqSuggestNewResult | null | undefined): boolean {
  if (!result) return false;
  if (result.shouldCreate) return true;
  if (result.absorbIntoDraftId) return true;
  return Boolean(result.mergeCandidates && result.mergeCandidates.length > 0);
}

/** 品题标题栏：品题浓缩结果列表入口 + 无题库红灯；详情跳转独立页 */
export function TtqIpttqToolbarButton({
  ttqaId,
  initialRecords,
  particleCount = 0,
  sessionUpdatedAt = null,
  particleAddedAts = [],
  sessionParticleKey = '',
}: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState<IpttqRecord[]>(initialRecords || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<IpttqSuggestNewResult | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [scanNote, setScanNote] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [creatingPhase, setCreatingPhase] = useState<'create' | 'skill'>('create');
  const [introFlash, setIntroFlash] = useState(false);
  const userEngagedRef = useRef(false);
  const autoCloseTimerRef = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const markEngaged = useCallback(() => {
    userEngagedRef.current = true;
    if (autoCloseTimerRef.current != null) {
      window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    setRecords(initialRecords || []);
  }, [initialRecords, ttqaId]);

  /** 进入详情：先弹出菜单对齐列表草图；无交互 3s 收起；空草稿闪一下 */
  useEffect(() => {
    userEngagedRef.current = false;
    setOpen(true);
    setIntroFlash(true);
    setLoading(true);
    setError(null);
    void ipttqService.listByTtqa(ttqaId)
      .then((data) => setRecords(data.records || []))
      .catch((err) => setError(err instanceof Error ? err.message : '加载品题浓缩结果失败'))
      .finally(() => setLoading(false));

    const flashTimer = window.setTimeout(() => setIntroFlash(false), 1600);
    autoCloseTimerRef.current = window.setTimeout(() => {
      autoCloseTimerRef.current = null;
      if (!userEngagedRef.current) setOpen(false);
    }, 3000);

    return () => {
      window.clearTimeout(flashTimer);
      if (autoCloseTimerRef.current != null) {
        window.clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [ttqaId]);

  useEffect(() => {
    if (suggestionNeedsAttention(suggestion)) markEngaged();
  }, [suggestion, markEngaged]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const runCoverageScan = useCallback(async (options?: { manual?: boolean }) => {
    const manual = Boolean(options?.manual);
    if (particleCount <= 0) {
      if (manual) {
        setError('品题尚无知识颗粒，请先加入参考题后再扫描或更新浓缩结果');
        setScanNote('请先加入知识颗粒');
      }
      return;
    }
    setSuggesting(true);
    setError(null);
    if (manual) setScanNote(null);
    try {
      const result = await ipttqService.suggestNew(ttqaId);
      if (suggestionNeedsAttention(result)) {
        setSuggestion(result);
        setScanNote(null);
        if (manual) setOpen(true);
      } else {
        setSuggestion(null);
        if (manual) {
          setScanNote(result.reason || '扫描完成：现有品题浓缩结果已基本覆盖，暂不建议新建。');
        }
      }
    } catch (err) {
      setSuggestion(null);
      if (manual) {
        setError(err instanceof Error ? err.message : '覆盖扫描失败');
      }
    } finally {
      setSuggesting(false);
    }
  }, [particleCount, ttqaId]);

  // 不再在进入品题 / 题量变化时自动 suggestNew；仅手动「重新扫描覆盖」

  const ipttqLastUpdatedAt = pickLatestIso(records.map((item) => item.updatedAt));
  const lastParticleAddedAt = pickLatestIso(particleAddedAts);
  const membershipChangedAt = pickLatestIso([sessionUpdatedAt, lastParticleAddedAt]);
  const driftedIpttqCount = sessionParticleKey
    ? countDriftedIpttqRecords(records, sessionParticleKey)
    : 0;
  const showScopeDriftTip = driftedIpttqCount > 0;

  const hasBankWarning = records.some((item) => ipttqNeedsBankWarning(item));
  const hasIskWarning = records.some((item) => ipttqNeedsIskWarning(item));
  const ipttqReady = isIpttqToolbarReady({ records, particleCount });
  const showPlainCreate = !hasCompleteGeneratedIpttq(records);
  const mergeCandidates = suggestion?.mergeCandidates || [];
  const showMergeBadge = mergeCandidates.length > 0;
  const absorbDraftId = String(suggestion?.absorbIntoDraftId || '').trim();
  const showAbsorbBadge = Boolean(absorbDraftId);
  const showCreateBadge = Boolean(suggestion?.shouldCreate) && !showAbsorbBadge;
  const showSuggestBadge = showCreateBadge || showAbsorbBadge || showMergeBadge;
  const proposedTitle = stripIpttqDomainTerms(
    String(suggestion?.proposedSkill?.mainTitle || '').trim(),
  );
  const proposedSubtitle = stripIpttqDomainTerms(
    String(suggestion?.proposedSkill?.subtitle || '').trim(),
  );
  const proposedLineage = stripIpttqDomainTerms(
    String(suggestion?.proposedSkill?.thinkingLineage || '').trim(),
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ipttqService.listByTtqa(ttqaId);
      setRecords(data.records || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载品题浓缩结果失败');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    markEngaged();
    setOpen((value) => {
      const next = !value;
      if (next) void load();
      return next;
    });
  };

  /** 按建议创建或吸入草稿：落库 proposedSkill → 立刻更新 Skill → 再进详情 */
  const handleCreateSuggested = async () => {
    const canCreate = Boolean(suggestion?.shouldCreate);
    const canAbsorb = Boolean(absorbDraftId);
    if ((!canCreate && !canAbsorb) || creating) return;
    if (particleCount <= 0) {
      setError('品题尚无知识颗粒，请先加入参考题后再创建或更新浓缩结果');
      setOpen(true);
      return;
    }
    setCreating(true);
    setCreatingPhase('create');
    setError(null);
    setOpen(false);
    try {
      const created = await ipttqService.create(ttqaId, suggestion?.proposedSkill);
      setCreatingPhase('skill');
      let finalRecord = created;
      try {
        const refreshed = await ipttqService.refresh(created.id, { scope: 'skill' });
        finalRecord = refreshed.record;
      } catch (refreshError) {
        console.warn('[TtqIpttqToolbarButton] skill refresh after suggest-create failed', refreshError);
      }
      setRecords((current) => {
        const without = current.filter((item) => item.id !== finalRecord.id);
        return [...without, finalRecord];
      });
      setSuggestion(null);
      navigate(`/ttq/${encodeURIComponent(ttqaId)}/ipttq/${encodeURIComponent(finalRecord.id)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : (canAbsorb ? '吸入草稿失败' : '创建失败'));
      setOpen(true);
    } finally {
      setCreating(false);
    }
  };

  const handleCreatePlainOriginalRef = async () => {
    if (creating) return;
    if (particleCount <= 0) {
      setError('品题尚无知识颗粒，无法创建不品题题库');
      setOpen(true);
      return;
    }
    setCreating(true);
    setCreatingPhase('create');
    setError(null);
    setOpen(false);
    try {
      const created = await ipttqService.createPlainOriginalRef(ttqaId);
      setRecords((current) => {
        const without = current.filter((item) => item.id !== created.id);
        return [...without, created];
      });
      navigate(`/ttq/${encodeURIComponent(ttqaId)}/ipttq/${encodeURIComponent(created.id)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建平凡品题失败');
      setOpen(true);
    } finally {
      setCreating(false);
    }
  };

  const emptyOrShell = !hasCompleteGeneratedIpttq(records);
  const flashEmpty = introFlash && emptyOrShell;

  return (
    <div className="relative" ref={rootRef}>
      {creating ? (
        <CreatingOverlay
          title={
            creatingPhase === 'create'
              ? (showAbsorbBadge ? '正在吸入草稿…' : '正在创建…')
              : '正在构造品题浓缩结果…'
          }
          detail={
            creatingPhase === 'create'
              ? (showAbsorbBadge
                ? (proposedTitle
                  ? `将「${proposedTitle}」方向写入现有草稿。`
                  : '正在把建议写入现有草稿。')
                : (proposedTitle
                  ? `以「${proposedTitle}」为方向新建品题浓缩结果。`
                  : '正在写入品题浓缩结果。'))
              : '根据本品题范畴与建议方向生成完整的品题浓缩结果，请稍候。'
          }
        />
      ) : null}

      <button
        type="button"
        className={
          'relative inline-flex h-8 items-center gap-1 rounded-lg border px-2 text-xs font-medium '
          + (hasBankWarning
            ? 'border-danger/40 bg-danger/5 text-danger'
            : hasIskWarning
              ? 'border-amber-400/50 bg-amber-50 text-amber-800'
              : ipttqReady
                ? 'border-success/35 bg-[#F1FAF1] text-success'
                : 'border-border bg-surface text-ink hover:bg-black/[0.03]')
          + (flashEmpty ? ' animate-pulse ring-2 ring-primary/50' : '')
        }
        onClick={handleOpen}
        onMouseEnter={markEngaged}
      >
        {hasBankWarning || hasIskWarning ? (
          <AlertCircle size={14} />
        ) : ipttqReady ? (
          <BadgeCheck size={14} />
        ) : null}
        品题浓缩结果
        {showSuggestBadge ? (
          <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary" />
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 z-40 mt-1 w-[min(92vw,26rem)] overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
          onMouseEnter={markEngaged}
          onPointerDown={markEngaged}
        >
          {showSuggestBadge ? (
            <div className="border-b border-border bg-canvas/60 px-2 py-2">
              <p className="text-[11px] leading-snug text-ink">
                {suggestion?.reason
                  || (showMergeBadge
                    ? '发现过度相似的品题浓缩结果，建议先合并。'
                    : showAbsorbBadge
                      ? '建议将新方向吸入现有草稿，避免并存多个草稿。'
                      : '品题范畴有变化，建议新建一条更紧凑的品题浓缩结果。')}
              </p>
              {!showMergeBadge && (showCreateBadge || showAbsorbBadge) ? (
                <>
                  {proposedTitle || proposedSubtitle || proposedLineage ? (
                    <div className="mt-1.5 rounded-md border border-border/70 bg-surface px-2 py-1.5">
                      {proposedTitle ? (
                        <p className="truncate text-[11px] font-medium text-ink">{proposedTitle}</p>
                      ) : null}
                      {proposedSubtitle ? (
                        <p className="mt-0.5 truncate text-[10px] text-muted">场景 · {proposedSubtitle}</p>
                      ) : null}
                      {proposedLineage ? (
                        <p className="mt-0.5 line-clamp-2 text-[10px] text-muted">{proposedLineage}</p>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-1.5 flex gap-1.5">
                    <button
                      type="button"
                      disabled={creating || particleCount <= 0}
                      className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-primary py-1 text-[11px] font-medium text-white disabled:opacity-50"
                      onClick={() => void handleCreateSuggested()}
                    >
                      {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                      {showAbsorbBadge ? '吸入草稿' : '按建议创建'}
                    </button>
                    <button
                      type="button"
                      disabled={creating}
                      className="rounded-md border border-border px-2 py-1 text-[11px] text-muted disabled:opacity-50"
                      onClick={() => setSuggestion(null)}
                    >
                      忽略
                    </button>
                  </div>
                </>
              ) : null}
              {showMergeBadge ? (
                <div className="mt-1.5 flex justify-end">
                  <button
                    type="button"
                    className="rounded-md border border-border px-2 py-1 text-[11px] text-muted"
                    onClick={() => setSuggestion(null)}
                  >
                    稍后
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          {suggesting ? (
            <p className="flex items-center gap-1 border-b border-border px-2 py-1.5 text-[10px] text-muted">
              <Loader2 size={10} className="animate-spin" />
              正在扫描现有品题浓缩结果是否覆盖全部内容…
            </p>
          ) : (
            <div className="space-y-0.5 border-b border-border px-2 py-1.5 text-[10px] leading-snug text-muted">
              <p>浓缩结果上次更新：{formatMetaTime(ipttqLastUpdatedAt)}</p>
              <p>知识颗粒最近加入：{formatMetaTime(lastParticleAddedAt)}</p>
              <p>品题范畴最近变更：{formatMetaTime(membershipChangedAt)}</p>
              {showScopeDriftTip ? (
                <p className="pt-0.5 text-amber-700">
                  {IPTTQ_SCOPE_DRIFT_TIP}
                  {driftedIpttqCount > 1 ? `（${driftedIpttqCount} 条）` : ''}
                </p>
              ) : null}
            </div>
          )}
          {scanNote && !suggesting ? (
            <p className="border-b border-border px-2 py-1.5 text-[10px] leading-snug text-muted">
              {scanNote}
            </p>
          ) : null}
          <div className="max-h-[min(70vh,28rem)] overflow-y-auto p-2">
            {showPlainCreate ? (
              <button
                type="button"
                disabled={creating || particleCount <= 0}
                className="mb-2 flex w-full items-center gap-1.5 rounded-md border border-border bg-canvas/60 px-2 py-2 text-left text-[11px] font-medium text-ink hover:bg-black/[0.03] disabled:opacity-50"
                onClick={() => void handleCreatePlainOriginalRef()}
              >
                <Plus size={12} />
                平凡品题 · 引用原始知识颗粒
              </button>
            ) : null}
            {loading ? (
              <p className="flex items-center gap-1 px-1 py-3 text-xs text-muted">
                <Loader2 size={12} className="animate-spin" />
                加载中…
              </p>
            ) : null}
            {error ? <p className="px-1 py-2 text-xs text-danger">{error}</p> : null}
            {!loading && records.length === 0 ? (
              <p
                className={
                  'rounded-md px-1 py-3 text-xs '
                  + (flashEmpty
                    ? 'animate-pulse bg-primary/10 font-medium text-primary ring-1 ring-primary/40'
                    : 'text-muted')
                }
              >
                暂无品题浓缩结果
              </p>
            ) : null}
            {records.map((record) => {
              const warn = ipttqNeedsBankWarning(record);
              const iskWarn = ipttqNeedsIskWarning(record);
              const isPlain = isIpttqSessionOriginalsRef(record);
              const isShellDraft = record.lifecycle === 'draft' && !hasCompleteGeneratedIpttq([record]);
              const scopeDrifted = Boolean(sessionParticleKey)
                && isIpttqScopeDrifted(record, sessionParticleKey);
              return (
                <div
                  key={record.id}
                  className={
                    'mb-1.5 rounded-md border bg-canvas/40 last:mb-0 '
                    + (flashEmpty && isShellDraft
                      ? 'animate-pulse border-primary/60 ring-2 ring-primary/35'
                      : scopeDrifted
                        ? 'border-amber-400/70'
                        : 'border-border/80')
                  }
                >
                  <button
                    type="button"
                    className="flex w-full items-start gap-1.5 px-2 py-1.5 text-left hover:bg-black/[0.03]"
                    onClick={() => {
                      setOpen(false);
                      navigate(`/ttq/${encodeURIComponent(ttqaId)}/ipttq/${encodeURIComponent(record.id)}`);
                    }}
                  >
                    {ipttqHasIskPreview(record) ? (
                      <IskToolbarThumb
                        imageUrl={record.skill?.intuitionSketch?.imageUrl}
                        imageOssId={record.skill?.intuitionSketch?.imageOssId}
                        alt={stripIpttqDomainTerms(record.skill.mainTitle) || '直觉草图'}
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[12px] font-medium text-ink">
                        {stripIpttqDomainTerms(record.skill.mainTitle) || '未命名模式'}
                        {isPlain ? (
                          <span className="ml-1 text-[10px] font-normal text-primary">平凡</span>
                        ) : null}
                        {record.isDefault ? (
                          <span className="ml-1 text-[10px] font-normal text-muted">默认</span>
                        ) : null}
                        {scopeDrifted ? (
                          <span className="ml-1 text-[10px] font-normal text-amber-700">建议更新</span>
                        ) : null}
                      </p>
                      {stripIpttqDomainTerms(record.skill.subtitle || '') ? (
                        <p className="truncate text-[10px] text-muted">
                          场景 · {stripIpttqDomainTerms(record.skill.subtitle || '')}
                        </p>
                      ) : null}
                      <p className={
                        'mt-0.5 text-[10px] '
                        + (warn ? 'text-danger' : iskWarn || scopeDrifted ? 'text-amber-700' : 'text-muted')
                      }>
                        {formatIpttqBankMetaLine(record, { warn })}
                        {iskWarn ? ` · ${ipttqIskWarningLabel(record)}` : ''}
                        {scopeDrifted ? ' · 范畴已变化' : ''}
                      </p>
                    </div>
                    <ChevronRight size={14} className="mt-0.5 shrink-0 text-muted" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end border-t border-border/60 px-2 py-1.5">
            <button
              type="button"
              disabled={suggesting || creating || particleCount <= 0}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-1.5 text-[10px] hover:bg-black/[0.04] disabled:opacity-50"
              onClick={() => void runCoverageScan({ manual: true })}
            >
              {suggesting ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <ScanSearch size={12} />
              )}
              重新扫描覆盖
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
